// Reference doc configuration that varies with reference entity (our different
// SDKs, the CLI, etc.)

export type SidebarItem = { label: string };

// Prefixes under which the published docs origin (and the `modal-docs` S3
// bucket) stores runtime-loaded reference surfaces: `<prefix>/latest` names the
// released version and `<prefix>/<version>/…` holds the content.
export const REFERENCE_PREFIXES = {
  pydoc: "sdk/py",
  jsdoc: "sdk/js",
  godoc: "sdk/go",
  cli: "cli",
} as const;

export type ReferencePrefix =
  (typeof REFERENCE_PREFIXES)[keyof typeof REFERENCE_PREFIXES];

// SDKs that publish release notes.
export type SdkLanguage = "py" | "js" | "go";

/** Subdirectories of a local generated-docs root (`inv web`) for each prefix. */
export const REFERENCE_LOCAL_DIRS: Record<ReferencePrefix, string> = {
  [REFERENCE_PREFIXES.pydoc]: "pydoc",
  [REFERENCE_PREFIXES.jsdoc]: "jsdoc",
  [REFERENCE_PREFIXES.godoc]: "godoc",
  [REFERENCE_PREFIXES.cli]: "cli",
};

export type ReferenceSurface = {
  // Display name in the navbar dropdown menu
  label: string;
  // Route base the per-item links hang off, and the surface's landing page.
  basePath: string;
  // URL prefix that marks this surface active, covering `basePath` and any
  // deeper pages so the navbar/sidebar stay highlighted across the whole area.
  activePrefix: string;
  // Heading for the reference list in the sidebar.
  sectionTitle: string;
  // Prefix under which runtime reference content is published.
  prefix: ReferencePrefix;
  // Page documenting this surface's releases, for surfaces that have one.
  // Surfaces may share a page: the `modal` CLI ships inside the Python client
  // package, so both point at the Python SDK releases page.
  releasesPath?: string;
  // Format applied to the entity label in the sidebar.
  formatSidebarLabel: (label: string) => string;
};

export const referenceSurfaces: ReferenceSurface[] = [
  {
    label: "Python SDK",
    basePath: "/docs/sdk/py/latest",
    activePrefix: "/docs/sdk/py",
    sectionTitle: "Python SDK Reference",
    prefix: REFERENCE_PREFIXES.pydoc,
    releasesPath: "/docs/sdk/py/releases",
    formatSidebarLabel: (label) => label,
  },
  {
    label: "JS SDK",
    basePath: "/docs/sdk/js/latest",
    activePrefix: "/docs/sdk/js",
    sectionTitle: "JavaScript SDK Reference",
    prefix: REFERENCE_PREFIXES.jsdoc,
    releasesPath: "/docs/sdk/js/releases",
    formatSidebarLabel: (label) => label,
  },
  {
    label: "Go SDK",
    basePath: "/docs/sdk/go/latest",
    activePrefix: "/docs/sdk/go",
    sectionTitle: "Go SDK Reference",
    prefix: REFERENCE_PREFIXES.godoc,
    releasesPath: "/docs/sdk/go/releases",
    formatSidebarLabel: (label) => label,
  },
  {
    label: "CLI",
    basePath: "/docs/cli/latest",
    activePrefix: "/docs/cli",
    sectionTitle: "CLI Reference",
    prefix: REFERENCE_PREFIXES.cli,
    releasesPath: "/docs/sdk/py/releases",
    formatSidebarLabel: (label) => `modal ${label}`,
  },
];

function collectReleasesPages(): { label: string; href: string }[] {
  const labelsByHref = new Map<string, string[]>();
  for (const surface of referenceSurfaces) {
    if (!surface.releasesPath) {
      continue;
    }
    // Every surface sharing a page is named in its label, so the page that
    // covers both the Python SDK and the CLI says so.
    labelsByHref.set(surface.releasesPath, [
      ...(labelsByHref.get(surface.releasesPath) ?? []),
      surface.label,
    ]);
  }
  return [...labelsByHref].map(([href, labels]) => ({
    label: labels.join(" & "),
    href,
  }));
}

/**
 * The distinct release-notes pages, in reference surface order, each labelled
 * with every surface it documents.
 */
export const releasesPages = collectReleasesPages();

/** The surface that owns a docs pathname; longest matching prefix wins. */
export function getReferenceSurfaceForPath(
  pathname: string,
): ReferenceSurface | undefined {
  return referenceSurfaces
    .filter((surface) => pathname.startsWith(surface.activePrefix))
    .sort((a, b) => b.activePrefix.length - a.activePrefix.length)[0];
}

// Unversioned roots that resolve to a landing page but are not owned by a single
// reference surface.
const extraRootRedirects: Record<string, string> = {
  // There is no language-agnostic SDK landing page yet; send visitors home.
  "/docs/sdk": "/docs",
};

/**
 * Redirect an unversioned docs root to its landing page. Each reference
 * surface's `activePrefix` (e.g. `/docs/sdk/js`) resolves to its `basePath`
 * (`/docs/sdk/js/latest`); a few roots with no single surface (e.g. `/docs/sdk`)
 * are handled explicitly. Returns `null` for anything else, including the
 * already-versioned pages under a surface.
 */
export function getUnversionedRootRedirect(pathname: string): string | null {
  // Normalize a single trailing slash so `/docs/sdk/js/` matches; SvelteKit's
  // trailing-slash canonicalization runs during routing, after this hook.
  const path =
    pathname.length > "/docs".length && pathname.endsWith("/")
      ? pathname.slice(0, -1)
      : pathname;
  const surface = referenceSurfaces.find(
    (candidate) => candidate.activePrefix === path,
  );
  // Guard against a surface whose landing page is its own root, which would
  // otherwise redirect to itself in a loop.
  if (surface && surface.basePath !== surface.activePrefix) {
    return surface.basePath;
  }
  return extraRootRedirects[path] ?? null;
}
